import React, { createContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import * as authService from "../services/auth";
import { UserCredentials, AuthUser } from "../types/auth";
import { getDashboardRoute } from "../utils/roleUtils";
import OneSignal from "react-onesignal";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  handleLogout: () => Promise<void>;
  navigateToDashboard: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Helper function to initialize OneSignal
  // This is called only after successful login to prevent premature subscription prompts
  const initializeOneSignal = async () => {
    // Check if already initialized to prevent double init
    if ((window as any).OneSignalInitialized) {
      return true;
    }

    try {
      // Mark as initializing
      (window as any).OneSignalInitialized = true;

      // Initialize OneSignal with configuration that works with our custom SW
      // The service worker is now automatically registered by VitePWA
      await OneSignal.init({
        appId: "c2149a7f-6c68-4edf-9ebe-7293c5aaeb36",
        allowLocalhostAsSecureOrigin: true,
        // Don't let OneSignal register its own SW since our SW imports OneSignal's
        autoRegister: false,
        autoResubscribe: false,
        notifyButton: { enable: false },
      });

      console.log("[OneSignal] Initialized successfully after login");
      return true;
    } catch (error) {
      // Reset flag on error so it can be retried
      (window as any).OneSignalInitialized = false;
      console.warn("[OneSignal] Initialization failed:", error);
      return false;
    }
  };

  // Helper function to identify user in OneSignal
  // IMPORTANT: Must be called AFTER OneSignal initialization
  // This ensures the external_id is set when the subscription is created
  const identifyUserInOneSignal = async (userId: string, userRole: string) => {
    const maxRetries = 10;
    const retryDelay = 500; // 500ms

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Wait for OneSignal to be initialized
        if (!(window as any).OneSignalInitialized) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }

        // Call login() to set the external_id
        // This associates the push subscription with our user
        await OneSignal.login(userId);

        // Small delay to allow OneSignal to process the login
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Add role tag for segmentation
        await OneSignal.User.addTag("role", userRole);

        return true;
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }
    console.warn("[OneSignal] Failed to identify user after retries");
    return false;
  };

  useEffect(() => {
    // Check if user is already logged in on app start
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const response = await authService.getCurrentUser();
          if (response.success && response.user) {
            setUser(response.user);

            // Identify user in OneSignal if they're already logged in
            // This ensures existing logged-in users get registered
            const userId = response.user.id || response.user._id;
            const userRole =
              response.user.role || response.user.roles?.[0] || "user";
            if (userId) {
              // Initialize OneSignal first, then identify user
              const initialized = await initializeOneSignal();
              if (initialized) {
                identifyUserInOneSignal(userId, userRole);
              }
            }

            // If we're on the login page and user is already authenticated,
            // redirect to appropriate dashboard
            if (
              window.location.pathname === "/login" ||
              window.location.pathname === "/"
            ) {
              const dashboardRoute = getDashboardRoute(response.user);
              navigate(dashboardRoute, { replace: true });
            }
          }
        }
      } catch (error) {
        // Token might be invalid, remove it
        console.error("Auth initialization error:", error);
        localStorage.removeItem("token");
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [navigate]);
  const handleLogin = async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    try {
      setLoading(true);
      const credentials: UserCredentials = { email, password };
      const response = await authService.login(credentials);

      if (response.success && response.user) {
        setUser(response.user);
        localStorage.setItem("token", response.token);

        // Initialize OneSignal first, then identify user and prompt for notifications
        const userId = response.user.id || response.user._id;
        const userRole =
          response.user.role || response.user.roles?.[0] || "user";

        try {
          if (userId) {
            // Step 1: Initialize OneSignal (only happens once per session)
            const initialized = await initializeOneSignal();

            if (initialized) {
              // Step 2: Identify user in OneSignal
              const identified = await identifyUserInOneSignal(
                userId,
                userRole,
              );

              if (identified) {
                // Step 3: Check permission and show prompt if needed
                const isPushEnabled = await OneSignal.Notifications.permission;
                if (!isPushEnabled) {
                  // Show the native subscription prompt
                  await OneSignal.Slidedown.promptPush();
                }
              }
            }
          }
        } catch (error) {
          // Silently fail - OneSignal may not be available (ad blocker, etc.)
          console.warn("[OneSignal] Post-login setup failed:", error);
        }

        // Navigate to dashboard immediately with the fresh user data
        const dashboardRoute = getDashboardRoute(response.user);
        navigate(dashboardRoute);

        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Login error:", error);
      // Re-throw the error with the backend message
      const message =
        error.response?.data?.message ||
        "Login failed. Please check your credentials.";
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      // Logout from OneSignal
      try {
        if ((window as any).OneSignalInitialized) {
          await OneSignal.logout();
        }
      } catch (error) {
        console.error("[OneSignal] Logout failed:", error);
      }

      await authService.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const navigateToDashboard = (userData?: AuthUser) => {
    const currentUser = userData || user;
    const dashboardRoute = getDashboardRoute(currentUser);
    navigate(dashboardRoute);
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await authService.getCurrentUser();
        if (response.success && response.user) {
          setUser(response.user);
        }
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login: handleLogin,
        handleLogout,
        navigateToDashboard,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export type { AuthContextType };
